// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: where the Tasks page is pointed at. The URL is the source of truth:
// /tasks, /tasks/mine|today|all, /tasks/space|folder|list/:id, plus ?view=.

export type WorkspaceLocationKind = 'home' | 'mine' | 'today' | 'all' | 'space' | 'folder' | 'list';
export type WorkspaceView = 'list' | 'board' | 'overview';

export interface WorkspaceLocation {
	kind: WorkspaceLocationKind;
	/** Space, folder or list id; only for those kinds. */
	id?: string;
}

const SECTIONS = new Set<WorkspaceLocationKind>(['mine', 'today', 'all']);
const CONTAINERS = new Set<WorkspaceLocationKind>(['space', 'folder', 'list']);

export function parseWorkspaceLocation(pathname: string): WorkspaceLocation {
	const parts = pathname.replace(/\/+$/, '').split('/').filter(Boolean);
	if (parts[0] !== 'tasks') return {kind: 'home'};
	const kind = parts[1] as WorkspaceLocationKind | undefined;
	if (!kind) return {kind: 'home'};
	if (SECTIONS.has(kind)) return {kind};
	if (CONTAINERS.has(kind) && parts[2]) return {kind, id: decodeURIComponent(parts[2])};
	return {kind: 'home'};
}

/** The views a location offers, first one is the default. */
export function viewsFor(location: WorkspaceLocation): Array<WorkspaceView> {
	if (location.kind === 'space') return ['overview', 'list', 'board'];
	if (location.kind === 'folder' || location.kind === 'list') return ['list', 'board'];
	return ['list'];
}

/** The ?view= asked for, else `fallback` (the view last used there), else the place's default. */
export function parseWorkspaceView(
	location: WorkspaceLocation,
	search: string | URLSearchParams,
	fallback?: WorkspaceView | null,
): WorkspaceView {
	const params = typeof search === 'string' ? new URLSearchParams(search) : search;
	const requested = params.get('view') as WorkspaceView | null;
	const options = viewsFor(location);
	if (requested && options.includes(requested)) return requested;
	return fallback && options.includes(fallback) ? fallback : options[0];
}

export function workspacePath(location: WorkspaceLocation, view?: WorkspaceView): string {
	let path = '/tasks';
	if (location.kind !== 'home') path += `/${location.kind}`;
	if (location.id && CONTAINERS.has(location.kind)) path += `/${encodeURIComponent(location.id)}`;
	if (view && view !== viewsFor(location)[0]) path += `?view=${view}`;
	return path;
}

/** Stable cache key for a task view (location + the filters that change the server query). */
export function workspaceKey(
	location: WorkspaceLocation,
	filters: {includeClosed?: boolean; mine?: boolean} = {},
): string {
	return [
		location.kind,
		location.id ?? '',
		filters.includeClosed ? 'closed' : 'open',
		filters.mine ? 'me' : 'any',
	].join(':');
}

export function sameLocation(a: WorkspaceLocation, b: WorkspaceLocation): boolean {
	return a.kind === b.kind && (a.id ?? '') === (b.id ?? '');
}

/** ?open=<task id>: the task to open (Open in new window, a task push). */
export const OPEN_TASK_PARAM = 'open';
/** The phone widgets' task links (`/tasks?task=<id>`, see konvo_mobile/NATIVE_CONTRACT.md). */
const LEGACY_TASK_PARAM = 'task';

/** The task a link asks to open, if any. */
export function linkedTaskId(search: string): string | null {
	const params = new URLSearchParams(search);
	return params.get(OPEN_TASK_PARAM) || params.get(LEGACY_TASK_PARAM) || null;
}

/** The same page without the task to open, so the next link to that task opens it again. */
export function withoutLinkedTask(pathname: string, search: string): string {
	const params = new URLSearchParams(search);
	params.delete(OPEN_TASK_PARAM);
	params.delete(LEGACY_TASK_PARAM);
	const rest = params.toString();
	return rest ? `${pathname}?${rest}` : pathname;
}

/** The page you are on with a task open beside it, for "Open in new window". */
export function workspaceTaskPath(pathname: string, search: string, taskId: string): string {
	const params = new URLSearchParams(search);
	params.set(OPEN_TASK_PARAM, taskId);
	return `${pathname}?${params}`;
}
